import { useLocation, useLoaderData } from '@remix-run/react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { type loader as rootLoader } from '#app/root.tsx'

interface NavigationContextValue {
	isWorkSlugPage: boolean
	isEducationSlugPage: boolean
	workNavigation: { slug: string | null }[] | null
	educationNavigation: { slug: string | null }[] | null
	currentSlug?: string
	shouldShowWorkNavigation: boolean
	shouldShowEducationNavigation: boolean
}

const NavigationContext = createContext<NavigationContextValue | undefined>(
	undefined,
)

export function NavigationProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const location = useLocation()
	const data = useLoaderData<typeof rootLoader>()
	const [currentSlug, setCurrentSlug] = useState<string | undefined>()

	// Extract slug from pathname
	useEffect(() => {
		const workSlugMatch = location.pathname.match(/^\/work\/([^\/]+)$/)
		const educationSlugMatch = location.pathname.match(
			/^\/education\/([^\/]+)$/,
		)
		setCurrentSlug(
			workSlugMatch
				? workSlugMatch[1]
				: educationSlugMatch
					? educationSlugMatch[1]
					: undefined,
		)
	}, [location.pathname])

	// Determine if we're on slug pages (not edit/create pages)
	const isWorkSlugPage =
		/^\/work\/[^\/]+$/.test(location.pathname) &&
		!location.pathname.includes('/edit') &&
		!location.pathname.includes('/add')

	const isEducationSlugPage =
		/^\/education\/[^\/]+$/.test(location.pathname) &&
		!location.pathname.includes('/edit') &&
		!location.pathname.includes('/add')

	const shouldShowWorkNavigation = Boolean(
		isWorkSlugPage &&
			data.workNavigation &&
			Array.isArray(data.workNavigation) &&
			data.workNavigation.length > 0 &&
			currentSlug,
	)

	const shouldShowEducationNavigation = Boolean(
		isEducationSlugPage &&
			data.educationNavigation &&
			Array.isArray(data.educationNavigation) &&
			data.educationNavigation.length > 0 &&
			currentSlug,
	)

	const value: NavigationContextValue = {
		isWorkSlugPage,
		isEducationSlugPage,
		workNavigation: data.workNavigation,
		educationNavigation: data.educationNavigation,
		currentSlug,
		shouldShowWorkNavigation,
		shouldShowEducationNavigation,
	}

	return (
		<NavigationContext.Provider value={value}>
			{children}
		</NavigationContext.Provider>
	)
}

export function useNavigationContext() {
	const context = useContext(NavigationContext)
	if (context === undefined) {
		throw new Error(
			'useNavigationContext must be used within a NavigationProvider',
		)
	}
	return context
}
